import { useState, useEffect } from 'react';
import { Phone, CheckCircle, PhoneOutgoing, Clock, Settings, CreditCard, RefreshCw, Loader2, Search, X } from '../../icons/wp-icons';
import AudioPlayer from '../Common/AudioPlayer';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEmbed } from '../../hooks/useEmbed';
import { censorName, censorBody, censorPhone } from '../../utils/demoCensor';
import { voiceAssistantApi } from '../../api/voiceAssistantApi';
import { kiZentraleApi } from '../../api/kiZentraleApi';
import { NotdienstSection } from './KiEinstellungen';
import { getVoiceAssistantSubscription, redirectToVoiceAssistantCheckout, cancelVoiceAssistantSubscription, reactivateVoiceAssistantSubscription } from '../../api/stripeApi';

const SUB_TABS = [
  { id: 'status', label: 'Status', icon: Phone },
  { id: 'einstellungen', label: 'Einstellungen', icon: Settings },
  { id: 'outbound', label: 'Ausgehende Anrufe', icon: PhoneOutgoing },
  { id: 'anrufe', label: 'Anrufe', icon: Clock },
];

export default function TelefonAgent({ readOnly, forcedSubTab = null, hideSubTabs = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEmbed } = useEmbed();
  const { user } = useAuth();
  // HEYKIKI-Orgs nutzen ElevenLabs statt Twilio/Stripe — kein WerkPilot-Abo
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  // Wenn `forcedSubTab` gesetzt ist, wird der Sub-Tab von außen gesteuert
  // (z.B. HeyKiki-Layout, wo Einstellungen/Ausgehend/Anrufe als Top-Level-Tabs
  // in KiZentrale stehen). Initial-State richtet sich dann nach dem Prop.
  const [activeTab, setActiveTab] = useState(forcedSubTab || 'status');

  // Sync mit externem Wechsel
  useEffect(() => {
    if (forcedSubTab) setActiveTab(forcedSubTab);
  }, [forcedSubTab]);
  const [settings, setSettings] = useState(null);
  // KiAgentSettings (Notdienst-Felder) — nur für Standalone, für den Notdienst-
  // Block in diesem Telefon-Tab. VoiceAssistantSettings liegen in `settings`.
  const [kiSettings, setKiSettings] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [callLogs, setCallLogs] = useState([]);
  const [callPage, setCallPage] = useState(1);
  const [callTotal, setCallTotal] = useState(0);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callDetail, setCallDetail] = useState(null);
  const [callDetailLoading, setCallDetailLoading] = useState(false);
  const [callSearchInput, setCallSearchInput] = useState('');
  const [callSearch, setCallSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provisioning, setProvisioning] = useState(false);

  // Nummernsuche
  const [areaCode, setAreaCode] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Tageszeit-abhängige Begrüßungen (HeyKiki: First-Message-Rules).
  // Backend-Endpoint ist HEYKIKI-only, daher Loader nur bei isHeykikiOrg feuern.
  const [firstMessageRules, setFirstMessageRules] = useState([]);
  const [newRuleDraft, setNewRuleDraft] = useState(null);

  useEffect(() => {
    loadSettings();
    if (isHeykikiOrg) loadFirstMessageRules();
  }, []);

  // Handle Stripe redirect params
  useEffect(() => {
    const subscriptionParam = searchParams.get('subscription');
    if (subscriptionParam === 'success') {
      toast.success('Telefonassistent-Abo erfolgreich aktiviert!');
      loadSettings();
      // Remove the param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('subscription');
      setSearchParams(newParams);
    } else if (subscriptionParam === 'canceled') {
      toast.error('Abo-Vorgang abgebrochen');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('subscription');
      setSearchParams(newParams);
    }
  }, []);

  // Debounce für Anruf-Suche
  useEffect(() => {
    const timer = setTimeout(() => {
      setCallPage(1);
      setCallSearch(callSearchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [callSearchInput]);

  useEffect(() => {
    if (activeTab === 'anrufe') loadCallLogs();
  }, [activeTab, callPage, callSearch]);

  // Auto-refresh wenn aktive Anrufe laufen
  useEffect(() => {
    const hasActiveCalls = callLogs.some(c => c.status === 'in-progress' || c.status === 'ringing');
    if (activeTab === 'anrufe' && hasActiveCalls) {
      const interval = setInterval(loadCallLogs, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab, callLogs]);

  const loadSettings = async () => {
    try {
      // HEYKIKI-Orgs haben kein Stripe-Abo (Feature läuft über HeyKiki-Partnerschaft) → kein Subscription-Call
      const [settingsData, subData, kiData] = await Promise.all([
        voiceAssistantApi.getSettings(),
        isHeykikiOrg ? Promise.resolve(null) : getVoiceAssistantSubscription().catch(() => null),
        // KiAgentSettings (Notdienst) nur für Standalone — HeyKiki zeigt den
        // Notdienst-Block in der KI-Zentrale-Einstellungsseite, nicht hier.
        isHeykikiOrg ? Promise.resolve(null) : kiZentraleApi.getSettings().catch(() => null),
      ]);
      setSettings(settingsData);
      setSubscription(subData);
      setKiSettings(kiData);
    } catch (err) {
      console.error('Telefon-Agent laden fehlgeschlagen:', err);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const loadCallLogs = async () => {
    try {
      const data = await voiceAssistantApi.getCallLogs({ limit: 20, offset: (callPage - 1) * 20, search: callSearch });
      setCallLogs(data.calls || data.items || []);
      setCallTotal(data.total || 0);
    } catch (err) {
      console.error('Anrufe laden fehlgeschlagen:', err);
    }
  };

  const loadCallDetail = async (call) => {
    if (selectedCall?.id === call.id) {
      setSelectedCall(null);
      setCallDetail(null);
      return;
    }
    setSelectedCall(call);
    setCallDetailLoading(true);
    try {
      const data = await voiceAssistantApi.getCallLog(call.id);
      setCallDetail(data);
    } catch (err) {
      setCallDetail(null);
    } finally {
      setCallDetailLoading(false);
    }
  };

  const handleSave = async (updates) => {
    if (readOnly) return;
    setSaving(true);
    try {
      const result = await voiceAssistantApi.updateSettings({ ...settings, ...updates });
      setSettings(result);
      toast.success('Gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Notdienst-Felder leben auf KiAgentSettings (NICHT VoiceAssistantSettings).
  // Speichern triggert den Standalone-ElevenLabs-Sync
  // (kiZentraleRoutes → syncElevenLabsIfHeykiki synct auch STANDALONE).
  const handleSaveKiSettings = async (updates) => {
    if (readOnly) return;
    setKiSettings(prev => ({ ...prev, ...updates }));
    try {
      const result = await kiZentraleApi.updateSettings(updates);
      setKiSettings(prev => ({ ...prev, ...result }));
      toast.success('Gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const loadFirstMessageRules = async () => {
    try {
      const data = await kiZentraleApi.getFirstMessageRules();
      setFirstMessageRules(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('FirstMessageRules laden fehlgeschlagen:', err);
    }
  };

  const handleSaveRule = async (rule) => {
    try {
      const payload = {
        name: rule.name,
        daysOfWeek: rule.daysOfWeek,
        startTime: rule.startTime,
        endTime: rule.endTime,
        message: rule.message,
        enabled: rule.enabled ?? true,
      };
      let saved;
      if (rule.id) {
        saved = await kiZentraleApi.updateFirstMessageRule(rule.id, payload);
        setFirstMessageRules(prev => prev.map(r => (r.id === saved.id ? saved : r)));
      } else {
        saved = await kiZentraleApi.createFirstMessageRule(payload);
        setFirstMessageRules(prev => [...prev, saved]);
        setNewRuleDraft(null);
      }
      toast.success(rule.id ? 'Regel aktualisiert' : 'Regel angelegt');
      return { ok: true };
    } catch (err) {
      const status = err.response?.status;
      const body = err.response?.data;
      const message = body?.error || err.message || 'Speichern fehlgeschlagen';
      if (status === 409) return { ok: false, error: message };
      toast.error(message);
      return { ok: false, error: message };
    }
  };

  const handleDeleteRule = async (rule) => {
    if (!rule.id) {
      setNewRuleDraft(null);
      return;
    }
    if (!window.confirm(`Regel „${rule.name}" wirklich löschen?`)) return;
    try {
      await kiZentraleApi.deleteFirstMessageRule(rule.id);
      setFirstMessageRules(prev => prev.filter(r => r.id !== rule.id));
      toast.success('Regel gelöscht');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const handleSearchNumbers = async () => {
    if (!areaCode || areaCode.length < 2) {
      toast.error('Bitte eine Vorwahl eingeben (mind. 2 Ziffern)');
      return;
    }
    setSearchingNumbers(true);
    setAvailableNumbers([]);
    setSelectedNumber(null);
    try {
      const data = await voiceAssistantApi.searchAvailableNumbers(areaCode);
      setAvailableNumbers(data.numbers || []);
      if ((data.numbers || []).length === 0) {
        toast('Keine Nummern mit dieser Vorwahl verfügbar. Versuche eine andere.', { icon: '📞' });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler bei der Nummernsuche');
    } finally {
      setSearchingNumbers(false);
    }
  };

  const handleProvision = async (phoneNumber = null) => {
    const numberToProvision = phoneNumber || selectedNumber;
    setProvisioning(true);
    try {
      toast.loading('Telefonnummer wird eingerichtet...');
      const result = await voiceAssistantApi.provision(numberToProvision);
      setSettings(prev => ({ ...prev, ...result }));
      toast.dismiss();
      toast.success('Telefonnummer erfolgreich eingerichtet!');
      setAvailableNumbers([]);
      setSelectedNumber(null);
      loadSettings();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.error || 'Fehler bei der Einrichtung');
    } finally {
      setProvisioning(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Möchten Sie das Telefonassistent-Abo wirklich kündigen?\n\nDas Abo wird sofort beendet und Ihre Telefonnummer wird gelöscht.')) {
      return;
    }
    setCancelling(true);
    try {
      await cancelVoiceAssistantSubscription();
      toast.success('Abo gekündigt und Telefonnummer gelöscht');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Kündigen');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      await reactivateVoiceAssistantSubscription();
      toast.success('Abo reaktiviert');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Reaktivieren');
    } finally {
      setReactivating(false);
    }
  };

  const handleToggleEnabled = async () => {
    handleSave({ enabled: !settings.enabled });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!settings) return null;

  // HEYKIKI: kein Stripe-Abo — Feature ist über HeyKiki-Partnerschaft freigeschaltet
  const hasActiveSubscription = isHeykikiOrg || subscription?.hasSubscription === true || subscription?.subscriptionStatus === 'ACTIVE' || subscription?.status === 'ACTIVE';
  const hasPhoneNumber = !!settings.phoneNumber;

  return (
    <div className="space-y-4">
      {/* Sub-Tabs (ausgeblendet wenn von außen gesteuert, z.B. HeyKiki-Layout) */}
      {!hideSubTabs && (
        <div className="flex overflow-x-auto gap-1 bg-gray-100 rounded-lg p-1">
          {SUB_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition ${
                  activeTab === tab.id
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Status Tab */}
      {activeTab === 'status' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Telefon-Agent Status</h3>
            {hasPhoneNumber && (
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-text-muted">{settings.enabled ? 'Aktiv' : 'Inaktiv'}</span>
                <div
                  onClick={!readOnly ? handleToggleEnabled : undefined}
                  className={`relative w-11 h-6 rounded-full transition cursor-pointer ${settings.enabled ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings.enabled ? 'translate-x-5' : ''}`} />
                </div>
              </label>
            )}
          </div>

          {/* Subscription Status — nicht für HEYKIKI-Orgs (Feature läuft über HeyKiki-Partnerschaft, nicht Stripe) */}
          {!isHeykikiOrg && (
          <div className="p-4 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-text-muted" />
                <div>
                  <p className="text-sm font-medium">Abo-Status</p>
                  <p className="text-xs text-text-muted">
                    {hasActiveSubscription ? 'Aktives Abo' :
                     subscription?.subscriptionStatus === 'CANCELLED' || subscription?.status === 'canceled' ? 'Gekündigt' : 'Kein Abo'}
                  </p>
                </div>
              </div>
              {!readOnly && !isEmbed && (
                <div className="flex gap-2">
                  {hasActiveSubscription ? (
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      className="px-3 py-1.5 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      {cancelling ? 'Wird gekündigt...' : 'Abo kündigen'}
                    </button>
                  ) : subscription?.subscriptionStatus === 'CANCELLED' || subscription?.status === 'canceled' ? (
                    <button
                      onClick={handleReactivateSubscription}
                      disabled={reactivating}
                      className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg text-sm font-medium hover:bg-yellow-700 transition disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {reactivating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      {reactivating ? 'Wird reaktiviert...' : 'Abo reaktivieren'}
                    </button>
                  ) : (
                    <button
                      onClick={() => redirectToVoiceAssistantCheckout()}
                      className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
                    >
                      Abo abschließen
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Telefonminuten Kontingent - nur bei aktivem Abo */}
          {hasActiveSubscription && settings.inklusivMinuten > 0 && (
            <div className="p-4 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-text-muted" />
                  <p className="text-sm font-medium">Telefonminuten</p>
                  <span className="text-xs text-text-muted">
                    {new Date().toLocaleString('de-DE', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
                {(settings.usedMinutes || 0) > settings.inklusivMinuten && (
                  <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                    +{(settings.usedMinutes || 0) - settings.inklusivMinuten} Zusatzmin. = {(((settings.usedMinutes || 0) - settings.inklusivMinuten) * (settings.preisProMinute || 0.50)).toFixed(2).replace('.', ',')} €
                  </span>
                )}
              </div>
              <div className="flex items-end gap-2 mb-2">
                <span className={`text-2xl font-bold ${(settings.usedMinutes || 0) >= settings.inklusivMinuten ? 'text-orange-600' : ''}`}>
                  {settings.usedMinutes || 0}
                </span>
                <span className="text-text-muted mb-0.5">/ {settings.inklusivMinuten} Min</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    (settings.usedMinutes || 0) > settings.inklusivMinuten
                      ? 'bg-orange-500'
                      : (settings.usedMinutes || 0) >= settings.inklusivMinuten * 0.8
                        ? 'bg-orange-500'
                        : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(100, ((settings.usedMinutes || 0) / settings.inklusivMinuten) * 100)}%` }}
                />
              </div>
              {(settings.usedMinutes || 0) >= settings.inklusivMinuten ? (
                <p className="text-xs text-orange-600 mt-1.5">Inklusivminuten aufgebraucht — Zusatzminuten: {(settings.preisProMinute || 0.50).toFixed(2).replace('.', ',')} €/Min</p>
              ) : (settings.usedMinutes || 0) >= settings.inklusivMinuten * 0.8 ? (
                <p className="text-xs text-orange-600 mt-1.5">Noch {settings.inklusivMinuten - (settings.usedMinutes || 0)} Minuten verbleibend</p>
              ) : null}
            </div>
          )}

          {/* Phone Number - nur wenn Abo aktiv UND Nummer vorhanden.
              Im Demo-Embed wird die konkrete Nummer ausgeblendet. */}
          {hasPhoneNumber && (
            <div className="p-4 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-text-muted" />
                <div>
                  <p className="text-sm font-medium">Telefonnummer</p>
                  <p className="text-sm text-text-muted">
                    {isEmbed ? '•••• ••• ••••' : settings.phoneNumber}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Telefonnummer einrichten - wenn Abo aktiv aber KEINE Nummer.
              Nicht für HEYKIKI-Orgs: die verwalten Nummern direkt in ElevenLabs, nicht via Twilio. */}
          {hasActiveSubscription && !hasPhoneNumber && !readOnly && !isHeykikiOrg && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Telefonnummer einrichten</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Suchen Sie eine lokale Telefonnummer anhand der Vorwahl Ihres Kunden.
                    </p>
                  </div>
                </div>
              </div>

              {/* Vorwahl-Suche */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-text-muted whitespace-nowrap">+49</span>
                    <input
                      type="text"
                      value={areaCode}
                      onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchNumbers()}
                      placeholder="Vorwahl z.B. 2365"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={handleSearchNumbers}
                  disabled={searchingNumbers || !areaCode || areaCode.length < 2}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition flex items-center gap-2 disabled:opacity-50"
                >
                  {searchingNumbers ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Suche...</>
                  ) : (
                    <><Search className="w-4 h-4" />Nummern suchen</>
                  )}
                </button>
              </div>

              {/* Ergebnisliste */}
              {availableNumbers.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b border-border flex items-center justify-between">
                    <span className="text-xs font-medium text-text-muted">{availableNumbers.length} Nummern gefunden</span>
                    {selectedNumber && (
                      <span className="text-xs text-primary font-medium">Ausgewählt: {selectedNumber}</span>
                    )}
                  </div>
                  <div className="max-h-60 overflow-y-auto divide-y divide-border">
                    {availableNumbers.map((num) => (
                      <button
                        key={num.phoneNumber}
                        onClick={() => setSelectedNumber(num.phoneNumber === selectedNumber ? null : num.phoneNumber)}
                        className={`w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-gray-50 transition ${
                          selectedNumber === num.phoneNumber ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                        }`}
                      >
                        <div>
                          <span className="text-sm font-mono font-medium">{censorPhone(num.phoneNumber, isEmbed)}</span>
                          {num.locality && (
                            <span className="text-xs text-text-muted ml-2">{num.locality}</span>
                          )}
                        </div>
                        {selectedNumber === num.phoneNumber && (
                          <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Einrichten Button */}
              {selectedNumber && (
                <button
                  onClick={() => handleProvision(selectedNumber)}
                  disabled={provisioning}
                  className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {provisioning ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Nummer wird eingerichtet...</>
                  ) : (
                    <><Phone className="w-4 h-4" />{selectedNumber} einrichten</>
                  )}
                </button>
              )}
            </div>
          )}

        </div>
      )}

      {/* Einstellungen Tab */}
      {activeTab === 'einstellungen' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          {/* Zugeordnete Telefonnummer zuerst — Kunden erwarten die prominent.
              Für HEYKIKI besonders wichtig, weil wir den Status-Tab ausgeblendet haben. */}
          {hasPhoneNumber && (
            <div className="p-4 rounded-lg border border-border bg-gray-50/50">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Ihre Telefonnummer</p>
                  <p className="text-base font-mono text-text-muted">
                    {isEmbed ? '•••• ••• ••••' : settings.phoneNumber}
                  </p>
                </div>
              </div>
            </div>
          )}

          <h3 className="text-lg font-semibold">Telefon-spezifische Einstellungen</h3>
          <p className="text-sm text-text-muted">
            Autonomiestufe, Pflichtfelder und Preisauskunft werden zentral unter dem Tab "Einstellungen" konfiguriert.
          </p>

          {/* Begrüßungstext: Wird bei Standalone-Orgs in den VAPI-Assistant
              synchronisiert, bei HEYKIKI-Orgs in den ElevenLabs-Agent
              (first_message). Einheitlich aus der KI-Zentrale steuerbar. */}
          <div>
            <label className="text-sm font-medium text-text block mb-1">Begrüßungstext</label>
            <textarea
              value={settings.greetingText || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, greetingText: e.target.value }))}
              onBlur={() => handleSave({ greetingText: settings.greetingText })}
              disabled={readOnly}
              rows={3}
              placeholder="Guten Tag, Sie erreichen..."
              className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-y"
            />
          </div>

          {/* Tageszeit-abhängige Begrüßungen — nur HeyKiki: läuft über den
              ElevenLabs Conversation-Init-Webhook. Per-Org konfigurierbare
              Regeln overriden die obige Standard-Begrüßung pro Zeitfenster. */}
          {isHeykikiOrg && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-text mb-1">
                <Clock className="w-4 h-4 text-primary" />
                Tageszeit-abhängige Begrüßungen (optional)
              </label>
              <p className="text-xs text-text-muted mb-3">
                Lege Regeln für bestimmte Wochentage und Zeitfenster an
                — z.B. „Feierabend" oder „Wochenende". Wenn keine Regel zur
                aktuellen Zeit passt, wird der obige Begrüßungstext verwendet.
              </p>

              <div className="space-y-3">
                {firstMessageRules.map(rule => (
                  <FirstMessageRuleCard
                    key={rule.id}
                    rule={rule}
                    onSave={handleSaveRule}
                    onDelete={handleDeleteRule}
                    readOnly={readOnly}
                  />
                ))}
                {newRuleDraft && (
                  <FirstMessageRuleCard
                    key="__draft__"
                    rule={newRuleDraft}
                    startInEditMode
                    onSave={handleSaveRule}
                    onDelete={handleDeleteRule}
                    readOnly={readOnly}
                  />
                )}
              </div>

              {!readOnly && !newRuleDraft && (
                <button
                  onClick={() => setNewRuleDraft({
                    name: '',
                    daysOfWeek: [1, 2, 3, 4, 5],
                    startTime: '17:00',
                    endTime: '08:00',
                    message: '',
                    enabled: true,
                  })}
                  className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:text-primary-dark"
                >
                  + Regel hinzufügen
                </button>
              )}
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-text block mb-1">Weiterleitungsnummer</label>
            <input
              type="tel"
              value={settings.forwardingNumber || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, forwardingNumber: e.target.value }))}
              onBlur={() => handleSave({ forwardingNumber: settings.forwardingNumber })}
              disabled={readOnly}
              placeholder="+49..."
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
            <p className="text-xs text-text-muted mt-1">Nummer für Weiterleitung an einen Menschen bei Bedarf</p>
          </div>

          {/* Notdienst-Block (Standalone): Seit 2026-06 telefonieren Standalone-
              Orgs über ElevenLabs (gleiche Agent-Config wie HeyKiki, kein orgType-
              Gate um renderNotdienst). Daher bekommen sie hier — an der Stelle, wo
              früher nur das einzelne „Notdienst-Nummer"-Feld stand — denselben
              vollen Notdienst-Container wie HeyKiki: Aktivierung, Keywords,
              Aktivzeiten, Aufpreis-Hinweis + Nummer. Nur mit neutralem, in der
              Org-Akzentfarbe einfärbbarem Icon statt Kiki-Maskottchen.
              Notdienst-Schalter/Keywords/Zeiten leben auf KiAgentSettings
              (`kiSettings`), die Notdienst-Nummer auf VoiceAssistantSettings
              (`settings`). Bei HeyKiki steht der Block in der KI-Zentrale-
              Einstellungsseite, daher hier nur für Standalone. */}
          {!isHeykikiOrg && kiSettings && (
            <NotdienstSection
              iconSrc="/kiki-icons/notdienst.svg?v=2"
              settings={kiSettings}
              setSettings={setKiSettings}
              handleSaveSettings={handleSaveKiSettings}
              voiceSettings={settings}
              handleSaveEmergencyNumber={(num) => handleSave({ emergencyForwardingNumber: num || null })}
              readOnly={readOnly}
            />
          )}

          <div>
            <label className="text-sm font-medium text-text block mb-1">Eingehende Weiterleitungsnummer</label>
            <input
              type="tel"
              value={settings.inboundForwardingNumber || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, inboundForwardingNumber: e.target.value }))}
              onBlur={() => handleSave({ inboundForwardingNumber: settings.inboundForwardingNumber })}
              disabled={readOnly}
              placeholder="+49..."
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
            <p className="text-xs text-text-muted mt-1">Büronummer, von der Anrufe weitergeleitet werden. Bei dieser Caller-ID fragt die KI nach Name und Telefonnummer.</p>
          </div>

          {/* Annahmezeiten für eingehende Anrufe —
              für HEYKIKI-Orgs ausgeblendet: ElevenLabs-Agent ist immer erreichbar,
              HeyKiki verwaltet Erreichbarkeit agentenseitig. */}
          {!isHeykikiOrg && (
          <div className="border-t border-border pt-4 space-y-3">
            <h4 className="text-sm font-semibold">Annahmezeiten</h4>

            <ToggleRow
              label="Anrufe immer annehmen"
              checked={settings.inboundImmerAktiv ?? true}
              onChange={(val) => {
                setSettings(prev => ({ ...prev, inboundImmerAktiv: val }));
                handleSave({ inboundImmerAktiv: val });
              }}
              disabled={readOnly}
            />

            {!(settings.inboundImmerAktiv ?? true) && (
              <div className="space-y-3 pl-1">
                <div>
                  <label className="text-sm font-medium text-text block mb-2">Wochentage</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { day: 1, label: 'Mo' },
                      { day: 2, label: 'Di' },
                      { day: 3, label: 'Mi' },
                      { day: 4, label: 'Do' },
                      { day: 5, label: 'Fr' },
                      { day: 6, label: 'Sa' },
                      { day: 0, label: 'So' },
                    ].map(({ day, label }) => {
                      const tage = (settings.inboundTage || '1,2,3,4,5').split(',').map(Number);
                      const active = tage.includes(day);
                      return (
                        <button
                          key={day}
                          type="button"
                          disabled={readOnly}
                          onClick={() => {
                            const newTage = active
                              ? tage.filter(d => d !== day)
                              : [...tage, day].sort((a, b) => a - b);
                            const newTageStr = newTage.join(',');
                            setSettings(prev => ({ ...prev, inboundTage: newTageStr }));
                            handleSave({ inboundTage: newTageStr });
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                            active
                              ? 'bg-primary text-white border-primary'
                              : 'bg-surface text-text-muted border-border hover:border-primary'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-text block mb-1">Standard-Zeiten</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={settings.inboundZeitVon || '08:00'}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, inboundZeitVon: e.target.value }));
                          handleSave({ inboundZeitVon: e.target.value });
                        }}
                        disabled={readOnly}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                      />
                      <span className="text-sm text-text-muted">–</span>
                      <input
                        type="time"
                        value={settings.inboundZeitBis || '18:00'}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, inboundZeitBis: e.target.value }));
                          handleSave({ inboundZeitBis: e.target.value });
                        }}
                        disabled={readOnly}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Individuelle Tageszeiten */}
                <div>
                  <label className="text-sm font-medium text-text block mb-2">Individuelle Zeiten pro Tag</label>
                  <p className="text-xs text-text-muted mb-2">Leer = Standard-Zeiten werden verwendet</p>
                  <div className="space-y-2">
                    {[
                      { day: 1, label: 'Montag' },
                      { day: 2, label: 'Dienstag' },
                      { day: 3, label: 'Mittwoch' },
                      { day: 4, label: 'Donnerstag' },
                      { day: 5, label: 'Freitag' },
                      { day: 6, label: 'Samstag' },
                      { day: 0, label: 'Sonntag' },
                    ].filter(({ day }) => {
                      const tage = (settings.inboundTage || '1,2,3,4,5').split(',').map(Number);
                      return tage.includes(day);
                    }).map(({ day, label }) => {
                      const tagesZeiten = settings.inboundTagesZeiten || {};
                      const tagZeit = tagesZeiten[String(day)];
                      const hasCustom = !!tagZeit;
                      return (
                        <div key={day} className="bg-surface-alt rounded-lg p-2.5 border border-border">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-medium">{label}</span>
                            {hasCustom && (
                              <button
                                type="button"
                                disabled={readOnly}
                                onClick={() => {
                                  const newTagesZeiten = { ...tagesZeiten };
                                  delete newTagesZeiten[String(day)];
                                  setSettings(prev => ({ ...prev, inboundTagesZeiten: newTagesZeiten }));
                                  handleSave({ inboundTagesZeiten: Object.keys(newTagesZeiten).length > 0 ? newTagesZeiten : null });
                                }}
                                className="text-xs text-text-muted hover:text-red-500 transition-colors"
                                title="Zurücksetzen auf Standard"
                              >
                                Zurücksetzen
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="time"
                              value={tagZeit?.von || ''}
                              placeholder={settings.inboundZeitVon || '08:00'}
                              onChange={(e) => {
                                const newTagesZeiten = { ...tagesZeiten };
                                if (e.target.value) {
                                  newTagesZeiten[String(day)] = {
                                    von: e.target.value,
                                    bis: tagZeit?.bis || settings.inboundZeitBis || '18:00'
                                  };
                                } else if (!tagZeit?.bis) {
                                  delete newTagesZeiten[String(day)];
                                } else {
                                  newTagesZeiten[String(day)] = { ...tagZeit, von: '' };
                                }
                                setSettings(prev => ({ ...prev, inboundTagesZeiten: newTagesZeiten }));
                                handleSave({ inboundTagesZeiten: Object.keys(newTagesZeiten).length > 0 ? newTagesZeiten : null });
                              }}
                              disabled={readOnly}
                              className="flex-1 min-w-0 px-2 py-1.5 border border-border rounded-lg text-sm"
                            />
                            <span className="text-sm text-text-muted">–</span>
                            <input
                              type="time"
                              value={tagZeit?.bis || ''}
                              placeholder={settings.inboundZeitBis || '18:00'}
                              onChange={(e) => {
                                const newTagesZeiten = { ...tagesZeiten };
                                if (e.target.value) {
                                  newTagesZeiten[String(day)] = {
                                    von: tagZeit?.von || settings.inboundZeitVon || '08:00',
                                    bis: e.target.value
                                  };
                                } else if (!tagZeit?.von) {
                                  delete newTagesZeiten[String(day)];
                              } else {
                                newTagesZeiten[String(day)] = { ...tagZeit, bis: '' };
                              }
                              setSettings(prev => ({ ...prev, inboundTagesZeiten: newTagesZeiten }));
                              handleSave({ inboundTagesZeiten: Object.keys(newTagesZeiten).length > 0 ? newTagesZeiten : null });
                            }}
                            disabled={readOnly}
                            className="flex-1 min-w-0 px-2 py-1.5 border border-border rounded-lg text-sm"
                          />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <p className="text-xs text-text-muted">
                  Außerhalb dieser Zeiten wird an die Weiterleitungsnummer verbunden.
                </p>
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {/* Outbound Tab */}
      {activeTab === 'outbound' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          <h3 className="text-lg font-semibold">Ausgehende Anrufe</h3>

          <ToggleRow
            label="Ausgehende Anrufe aktiviert"
            checked={settings.outboundCallsEnabled}
            onChange={(val) => handleSave({ outboundCallsEnabled: val })}
            disabled={readOnly}
          />

          {settings.outboundCallsEnabled && (
            <>
              <div className="border-t border-border pt-4 space-y-3">
                <h4 className="text-sm font-semibold">Anlässe</h4>
                <ToggleRow label="Terminbestätigung" checked={settings.terminBestaetigung_aktiv} onChange={(val) => handleSave({ terminBestaetigung_aktiv: val })} disabled={readOnly} />
                <ToggleRow label="Terminerinnerung" checked={settings.terminErinnerung_aktiv} onChange={(val) => handleSave({ terminErinnerung_aktiv: val })} disabled={readOnly} />
                {settings.terminErinnerung_aktiv && (
                  <div className="ml-8 flex items-center gap-2">
                    <label className="text-sm text-text-muted whitespace-nowrap">Erinnerung</label>
                    <select
                      value={settings.terminErinnerung_vorStunden || 24}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setSettings(prev => ({ ...prev, terminErinnerung_vorStunden: val }));
                        handleSave({ terminErinnerung_vorStunden: val });
                      }}
                      disabled={readOnly}
                      className="px-3 py-1.5 border border-border rounded-lg text-sm"
                    >
                      <option value={1}>1 Stunde</option>
                      <option value={2}>2 Stunden</option>
                      <option value={3}>3 Stunden</option>
                      <option value={4}>4 Stunden</option>
                      <option value={6}>6 Stunden</option>
                      <option value={12}>12 Stunden</option>
                      <option value={24}>24 Stunden</option>
                      <option value={48}>48 Stunden</option>
                    </select>
                    <span className="text-sm text-text-muted">vorher</span>
                  </div>
                )}
                <ToggleRow label="Terminabsage" checked={settings.terminAbsage_aktiv} onChange={(val) => handleSave({ terminAbsage_aktiv: val })} disabled={readOnly} />
                <ToggleRow label="Verspätung" checked={settings.verspaetung_aktiv} onChange={(val) => handleSave({ verspaetung_aktiv: val })} disabled={readOnly} />
                <ToggleRow label="Mahnung" checked={settings.mahnung_aktiv} onChange={(val) => handleSave({ mahnung_aktiv: val })} disabled={readOnly} />
                <ToggleRow label="KVA nachfassen" checked={settings.kvaNachfassen_aktiv} onChange={(val) => handleSave({ kvaNachfassen_aktiv: val })} disabled={readOnly} />
              </div>

              <div className="border-t border-border pt-4">
                <h4 className="text-sm font-semibold mb-3">Zeitfenster</h4>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-xs text-text-muted">Anrufe von</label>
                    <input type="time" value={settings.anrufZeitVon || '09:00'} onChange={(e) => setSettings(prev => ({ ...prev, anrufZeitVon: e.target.value }))} onBlur={() => handleSave({ anrufZeitVon: settings.anrufZeitVon })} disabled={readOnly} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-text-muted">Anrufe bis</label>
                    <input type="time" value={settings.anrufZeitBis || '18:00'} onChange={(e) => setSettings(prev => ({ ...prev, anrufZeitBis: e.target.value }))} onBlur={() => handleSave({ anrufZeitBis: settings.anrufZeitBis })} disabled={readOnly} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                  </div>
                </div>

                {/* Anruf-Tage */}
                <div>
                  <label className="text-xs text-text-muted block mb-2">Erlaubte Wochentage</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { day: 1, label: 'Mo' },
                      { day: 2, label: 'Di' },
                      { day: 3, label: 'Mi' },
                      { day: 4, label: 'Do' },
                      { day: 5, label: 'Fr' },
                      { day: 6, label: 'Sa' },
                      { day: 0, label: 'So' }
                    ].map(({ day, label }) => {
                      const aktiveTage = (settings.anrufTage || '1,2,3,4,5').split(',').filter(Boolean).map(Number);
                      const isActive = aktiveTage.includes(day);
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => {
                            if (readOnly) return;
                            const neueTage = isActive
                              ? aktiveTage.filter(t => t !== day)
                              : [...aktiveTage, day];
                            const sorted = neueTage.sort((a, b) => a - b).join(',');
                            setSettings(prev => ({ ...prev, anrufTage: sorted }));
                            handleSave({ anrufTage: sorted });
                          }}
                          disabled={readOnly}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                            isActive
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          } disabled:opacity-50`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-text-muted mt-2">
                    Ausgehende Anrufe werden nur an den ausgewählten Tagen innerhalb des Zeitfensters getätigt. Außerhalb wird auf den nächsten erlaubten Slot verschoben.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Anrufe Tab */}
      {activeTab === 'anrufe' && (
        <div className="bg-surface rounded-card shadow-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Anruf-Protokoll</h3>
            <button onClick={loadCallLogs} className="p-2 text-text-muted hover:text-text">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Suche nach Name, Nummer, Zusammenfassung..."
              value={callSearchInput}
              onChange={(e) => setCallSearchInput(e.target.value)}
              className="w-full pl-9 pr-9 py-2 border border-border rounded-lg text-sm"
            />
            {callSearchInput && (
              <button
                onClick={() => { setCallSearchInput(''); setCallSearch(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {callLogs.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-8">{callSearch ? 'Keine Anrufe gefunden' : 'Keine Anrufe vorhanden'}</p>
          ) : (
            <div className="space-y-2">
              {callLogs.map((call) => (
                <div key={call.id}>
                  <div
                    onClick={() => loadCallDetail(call)}
                    className={`p-3 rounded-lg border transition cursor-pointer ${
                      selectedCall?.id === call.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-1.5 rounded-full ${
                          (call.status === 'in-progress' || call.status === 'ringing')
                            ? 'bg-green-100 animate-pulse'
                            : call.direction === 'outbound' ? 'bg-blue-100' : 'bg-purple-100'
                        }`}>
                          {call.direction === 'outbound' ? <PhoneOutgoing className={`w-3.5 h-3.5 ${(call.status === 'in-progress' || call.status === 'ringing') ? 'text-green-600' : 'text-blue-600'}`} /> : <Phone className={`w-3.5 h-3.5 ${(call.status === 'in-progress' || call.status === 'ringing') ? 'text-green-600' : 'text-purple-600'}`} />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {call.direction === 'outbound' ? 'Ausgehender Anruf an ' : 'Eingehender Anruf von '}
                            {(call.customerName || call.kunde?.name)
                              ? <>{censorName(call.customerName || call.kunde?.name, isEmbed)}{(call.phoneNumber || call.callerNumber) && !['Unbekannt', 'anonymous'].includes(call.phoneNumber || call.callerNumber) && <span className="text-text-muted font-normal"> ({censorPhone(call.phoneNumber || call.callerNumber, isEmbed)})</span>}</>
                              : (censorPhone(call.phoneNumber || call.callerNumber, isEmbed) || 'Unbekannt')
                            }
                            {(call.status === 'in-progress' || call.status === 'ringing') && (
                              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                                Im Gespräch
                              </span>
                            )}
                          </p>
                          {call.direction === 'outbound' && call.anlassTyp && (
                            <p className="text-xs text-text-muted">
                              {call.anlassTyp === 'TERMIN_BESTAETIGUNG' ? 'Terminbestätigung' :
                               call.anlassTyp === 'TERMIN_ERINNERUNG' ? 'Terminerinnerung' :
                               call.anlassTyp === 'TERMIN_ABSAGE' ? 'Terminabsage' :
                               call.anlassTyp === 'VERSPAETUNG' ? 'Verspätung' :
                               call.anlassTyp === 'MAHNUNG' ? 'Mahnung' :
                               call.anlassTyp === 'KVA_NACHFASSEN' ? 'KVA nachfassen' :
                               call.anlassTyp}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-text-muted">{new Date(call.startedAt || call.createdAt).toLocaleString('de-DE')}</p>
                        {(call.status === 'in-progress' || call.status === 'ringing') ? (
                          <p className="text-xs text-green-600 font-medium">Läuft...</p>
                        ) : (
                          <p className="text-xs text-text-muted">{call.durationSeconds ? `${Math.floor(call.durationSeconds / 60)}:${String(call.durationSeconds % 60).padStart(2, '0')}` : '-'}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {selectedCall?.id === call.id && (
                    <div className="mt-1 p-4 rounded-lg border border-primary/20 bg-white space-y-3">
                      {callDetailLoading ? (
                        <div className="flex justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        </div>
                      ) : callDetail ? (
                        <>
                          {(callDetail.recordingUrl || call.recordingUrl) && (
                            <div>
                              <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Aufzeichnung</p>
                              <AudioPlayer src={callDetail.recordingUrl || call.recordingUrl} />
                            </div>
                          )}
                          {(callDetail.summary || call.summary) && (
                            <div>
                              <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Zusammenfassung</p>
                              <p className="text-sm text-text">{censorBody(callDetail.summary || call.summary, isEmbed)}</p>
                            </div>
                          )}
                          {callDetail.transcript && (
                            <div>
                              <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Transkript</p>
                              <div className="max-h-64 overflow-y-auto rounded-lg bg-gray-50 p-3">
                                {isEmbed ? (
                                  <p className="text-sm whitespace-pre-wrap">Transkript (für Demo zensiert)</p>
                                ) : typeof callDetail.transcript === 'string' ? (
                                  <p className="text-sm whitespace-pre-wrap">{callDetail.transcript}</p>
                                ) : Array.isArray(callDetail.transcript) ? (
                                  <div className="space-y-2">
                                    {callDetail.transcript.map((entry, idx) => (
                                      <div key={idx} className={`text-sm ${entry.role === 'assistant' || entry.speaker === 'assistant' ? 'text-primary' : 'text-text'}`}>
                                        <span className="font-medium text-xs uppercase">{entry.role === 'assistant' || entry.speaker === 'assistant' ? 'KI' : 'Anrufer'}:</span>{' '}
                                        {entry.content || entry.text || entry.message}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(callDetail.transcript, null, 2)}</pre>
                                )}
                              </div>
                            </div>
                          )}
                          {callDetail.extractedData && (() => {
                            const HIDDEN_FOR_HEYKIKI = new Set(['source', 'agentId', 'agent_id', 'conversationId', 'conversation_id', 'callSuccessful', 'call_successful']);
                            const entries = Object.entries(callDetail.extractedData)
                              .filter(([k]) => !(isHeykikiOrg && HIDDEN_FOR_HEYKIKI.has(k)));
                            if (entries.length === 0) return null;
                            return (
                              <div>
                                <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Extrahierte Daten</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {entries.map(([key, value]) => (
                                    <div key={key} className="text-sm">
                                      <span className="text-text-muted">{key}:</span> <span className="font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                          {!callDetail.summary && !callDetail.transcript && (
                            <p className="text-sm text-text-muted">Keine weiteren Details verfügbar.</p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-text-muted">Details konnten nicht geladen werden.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {callTotal > 20 && (
                <div className="flex items-center justify-center gap-2 pt-3">
                  <button onClick={() => setCallPage(p => Math.max(1, p - 1))} disabled={callPage <= 1} className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50">Zurück</button>
                  <span className="text-sm text-text-muted">Seite {callPage}</span>
                  <button onClick={() => setCallPage(p => p + 1)} disabled={callLogs.length < 20} className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50">Weiter</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, disabled }) {
  return (
    <label className="flex items-center justify-between p-3 rounded-lg border border-border cursor-pointer">
      <span className="text-sm font-medium">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="accent-primary w-5 h-5"
      />
    </label>
  );
}

// ============================================================
// FirstMessageRuleCard
// ============================================================
// Anzeige- und Edit-Komponente für eine einzelne tageszeit-abhängige
// Begrüßungsregel. Cross-Midnight (endTime < startTime) wird in der
// Anzeige als „bis morgen früh" markiert.

const WEEKDAY_LABELS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function FirstMessageRuleCard({ rule, onSave, onDelete, startInEditMode = false, readOnly = false }) {
  const [editing, setEditing] = useState(startInEditMode);
  const [draft, setDraft] = useState({
    name: rule.name || '',
    daysOfWeek: Array.isArray(rule.daysOfWeek) ? [...rule.daysOfWeek] : [1, 2, 3, 4, 5],
    startTime: rule.startTime || '17:00',
    endTime: rule.endTime || '08:00',
    message: rule.message || '',
    enabled: rule.enabled ?? true,
  });
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);

  const isCrossMidnight = draft.endTime < draft.startTime;

  const toggleDay = (day) => {
    setDraft(prev => {
      const has = prev.daysOfWeek.includes(day);
      return {
        ...prev,
        daysOfWeek: has ? prev.daysOfWeek.filter(d => d !== day) : [...prev.daysOfWeek, day].sort(),
      };
    });
  };

  const handleSubmit = async () => {
    setServerError(null);
    setSaving(true);
    const result = await onSave({ ...draft, id: rule.id });
    setSaving(false);
    if (result.ok) {
      setEditing(false);
    } else {
      setServerError(result.error);
    }
  };

  const handleCancel = () => {
    if (!rule.id) {
      onDelete(rule);
      return;
    }
    setDraft({
      name: rule.name,
      daysOfWeek: [...rule.daysOfWeek],
      startTime: rule.startTime,
      endTime: rule.endTime,
      message: rule.message,
      enabled: rule.enabled,
    });
    setServerError(null);
    setEditing(false);
  };

  const handleToggleEnabled = async (next) => {
    setServerError(null);
    const result = await onSave({ ...draft, enabled: next, id: rule.id });
    if (result.ok) {
      setDraft(prev => ({ ...prev, enabled: next }));
    } else {
      setServerError(result.error);
    }
  };

  if (!editing) {
    const sortedDays = [...rule.daysOfWeek].sort();
    const allWeekdays = sortedDays.length === 5 && sortedDays.every((d, i) => d === i + 1);
    const allWeekend = sortedDays.length === 2 && sortedDays.includes(0) && sortedDays.includes(6);
    const allWeek = sortedDays.length === 7;
    const dayLabel = allWeek ? 'Täglich'
      : allWeekdays ? 'Mo–Fr'
      : allWeekend ? 'Sa+So'
      : sortedDays.map(d => WEEKDAY_LABELS[d]).join(', ');

    return (
      <div className={`border border-border rounded-lg p-3 ${rule.enabled ? 'bg-white' : 'bg-gray-50 opacity-70'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm">{rule.name}</span>
              {!rule.enabled && <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">Aus</span>}
            </div>
            <div className="text-xs text-text-muted mb-2">
              {dayLabel} · {rule.startTime}–{rule.endTime}
              {rule.endTime < rule.startTime && ' (bis morgen früh)'}
            </div>
            <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">{rule.message}</div>
          </div>
          {!readOnly && (
            <div className="flex flex-col gap-2 shrink-0">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  onChange={(e) => handleToggleEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="relative w-9 h-5 bg-gray-300 peer-checked:bg-primary rounded-full transition">
                  <div className={`absolute top-0.5 left-0.5 bg-white w-4 h-4 rounded-full transition ${rule.enabled ? 'translate-x-4' : ''}`} />
                </div>
              </label>
              <div className="flex gap-1">
                <button
                  onClick={() => setEditing(true)}
                  className="p-1.5 text-text-muted hover:text-primary"
                  title="Bearbeiten"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(rule)}
                  className="p-1.5 text-text-muted hover:text-red-600"
                  title="Löschen"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
        {serverError && <p className="text-xs text-red-600 mt-2">{serverError}</p>}
      </div>
    );
  }

  return (
    <div className="border border-primary/40 rounded-lg p-3 bg-primary/5">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Name</label>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => setDraft(prev => ({ ...prev, name: e.target.value }))}
            placeholder="z.B. Feierabend"
            className="w-full px-3 py-1.5 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Wochentage</label>
          <div className="flex gap-1 flex-wrap">
            {WEEKDAY_LABELS.map((label, idx) => {
              const active = draft.daysOfWeek.includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleDay(idx)}
                  className={`px-2.5 py-1 text-xs rounded border transition ${
                    active
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-text-muted border-border hover:border-primary/40'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-text-muted mb-1">Von</label>
            <input
              type="time"
              value={draft.startTime}
              onChange={(e) => setDraft(prev => ({ ...prev, startTime: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-text-muted mb-1">Bis</label>
            <input
              type="time"
              value={draft.endTime}
              onChange={(e) => setDraft(prev => ({ ...prev, endTime: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
        {isCrossMidnight && (
          <p className="text-xs text-text-muted -mt-1">
            Endzeit liegt vor Startzeit — die Regel gilt von {draft.startTime} bis {draft.endTime} am Folgetag.
          </p>
        )}

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Begrüßungstext</label>
          <textarea
            value={draft.message}
            onChange={(e) => setDraft(prev => ({ ...prev, message: e.target.value }))}
            rows={3}
            placeholder="z.B. Guten Abend, Sie erreichen außerhalb unserer Geschäftszeiten…"
            className="w-full px-3 py-1.5 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {serverError && <p className="text-xs text-red-600">{serverError}</p>}

        <div className="flex gap-2 justify-end">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-text-muted hover:text-text"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50 inline-flex items-center gap-1"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
